import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { DONATE_URL } from "@/lib/links";
import { motion } from "framer-motion";
import { GraduationCap, Church, Flag, Shield, Home, Ticket, Users, Landmark, Building2, Star, Heart, Globe, ArrowRight } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { PageHero } from "@/components/PageHero";
import { api, API } from "@/lib/api";

const HERO = "https://images.pexels.com/photos/7649100/pexels-photo-7649100.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=1000&w=1600";

export const DIVISION_ICONS = { GraduationCap, Church, Flag, Shield, Home, Ticket, Users, Landmark, Building2, Star, Heart, Globe };

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.08 } }),
};

export default function Divisions() {
  const [divisions, setDivisions] = useState([]);

  useEffect(() => {
    api.get("/divisions").then(({ data }) => setDivisions(data)).catch(() => {});
  }, []);

  return (
    <div>
      <Navbar />

      <PageHero
        image={HERO}
        imageAlt="American flag"
        kicker="Our Departments"
        title={<>Our <span className="text-[#D90429]">Divisions</span></>}
        subtitle="Specialized teams, one mission. Each division focuses its training and resources where protection is needed most — from classrooms and sanctuaries to city streets and national events."
      />

      <section className="py-24">
        <div className="max-w-7xl mx-auto px-5 sm:px-8 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {divisions.map((d, i) => {
            const Icon = DIVISION_ICONS[d.icon] || Shield;
            return (
              <motion.div
                key={d.id}
                initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i}
                data-testid="division-card"
                className="group border border-slate-200 hover:border-[#D90429] hover:shadow-lg transition-all duration-200 flex flex-col overflow-hidden"
              >
                {d.image_file_id && (
                  <div className="h-44 overflow-hidden bg-slate-100">
                    <img src={`${API}/public/files/${d.image_file_id}`} alt={d.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300" />
                  </div>
                )}
                <div className="p-8 flex flex-col flex-1">
                  {!d.image_file_id && (
                    <div className="w-14 h-14 bg-[#0A1128] flex items-center justify-center mb-6 group-hover:bg-[#D90429] transition-colors duration-200">
                      <Icon className="w-7 h-7 text-white" strokeWidth={1.75} />
                    </div>
                  )}
                  <h3 className="font-display text-2xl uppercase leading-tight mb-3">{d.title}</h3>
                  <p className="text-slate-600 leading-relaxed flex-1">{d.text}</p>
                  <Link to={d.slug ? `/divisions/${d.slug}` : "/divisions"} data-testid="division-detail-link" className="inline-flex items-center gap-2 mt-6 text-[#0A1128] font-bold uppercase tracking-[0.15em] text-xs border-b-2 border-[#D90429] pb-1 self-start group-hover:gap-4 transition-all duration-200">
                     Learn More <ArrowRight className="w-4 h-4" />
                  </Link>
                </div>
              </motion.div>
            );
          })}
        </div>
      </section>

      <section className="bg-[#0A1128] text-white py-20 grain stars relative">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center relative z-10">
          <Shield className="w-10 h-10 text-[#D90429] mx-auto mb-6" />
          <h2 className="font-display text-4xl sm:text-5xl uppercase leading-[0.95]">Find Your Place In The Ranks</h2>
          <p className="text-white/70 mt-5 max-w-xl mx-auto">Whatever your skills, there's a division that needs you. Join our newsletter to stay in the loop on trainings and where you can serve.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center mt-8">
            <Link to="/join" data-testid="divisions-join-button" className="bg-white text-[#0A1128] px-8 py-4 uppercase tracking-[0.15em] text-sm font-bold hover:bg-[#F4A261] transition-colors duration-200 active:scale-95">Join Our Newsletter</Link>
            <a href={DONATE_URL} target="_blank" rel="noopener noreferrer" data-testid="divisions-donate-button" className="inline-flex items-center justify-center gap-2 bg-[#D90429] text-white px-8 py-4 uppercase tracking-[0.15em] text-sm font-bold hover:bg-[#b0031f] transition-colors duration-200 active:scale-95">Fund The Mission <ArrowRight className="w-4 h-4" /></a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
